if (!customElements.get('facet-custom-select')) {
  class FacetCustomSelect extends HTMLElement {
    constructor () {
      super();
      this.custom = null;
      this.placeholder = this.getAttribute('data-placeholder') || '';
      this.handleChangeBound = this.handleChange.bind(this);
      this.handleResizeBound = this.handleResize.bind(this);
      this.activePlaceholder = null;
      this.fitSelectedItemFrame = null;
    }

    isMobile () {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    handleChange () {
      this.scheduleFitSelectedItem();
      const form = this.closest('form');
      if (form) form.dispatchEvent(new Event('input'));
    }

    scheduleFitSelectedItem () {
      if (this.fitSelectedItemFrame) cancelAnimationFrame(this.fitSelectedItemFrame);
      this.fitSelectedItemFrame = requestAnimationFrame(() => {
        this.fitSelectedItemFrame = null;
        this.fitSelectedItem();
      });
    }

    resetSelectedItemFit () {
      this.style.removeProperty('--facets-selected-option-font-size');
      this.closest('facet-filters-form.facets')?.style.removeProperty('--facets-sorting-field-gap');
    }

    getSelectedItemFitContext () {
      const toolbar = this.closest('.facets-container--sorting-filter');
      const sortControl = this.closest('facet-filters-form.facets');
      const field = this.closest('.facet-filters__field');
      const label = field?.querySelector('.facet-filters__label');

      if (
        !toolbar ||
        !sortControl ||
        !field ||
        !label ||
        !window.matchMedia('(min-width: 990px)').matches
      ) {
        return null;
      }

      return {
        sortControl,
        field,
        label,
      };
    }

    getSelectedItemFit (measurement) {
      const context = this.getSelectedItemFitContext();
      if (!context || !measurement) return null;

      const { sortControl, field, label } = context;
      const { selectedWidth, baseFontSize, listInlinePadding } = measurement;
      const sortControlWidth = sortControl.getBoundingClientRect().width;
      const labelWidth = label.getBoundingClientRect().width;
      const fieldStyles = window.getComputedStyle(field);
      const preferredGap = parseFloat(fieldStyles.columnGap || fieldStyles.gap) || 0;
      const minGap = Math.min(15, window.innerWidth * 0.0098, preferredGap);
      const arrowWidth = 16;
      let fieldGap = preferredGap;
      let availableWidth = sortControlWidth - labelWidth - fieldGap - listInlinePadding - arrowWidth;

      if (!baseFontSize || selectedWidth <= availableWidth) return;

      const fit = {
        sortControl,
        fieldGap: null,
        fontSize: null,
      };

      if (preferredGap > minGap) {
        const availableWidthWithMinGap = sortControlWidth - labelWidth - minGap - listInlinePadding - arrowWidth;
        fieldGap = selectedWidth <= availableWidthWithMinGap
          ? Math.max(minGap, sortControlWidth - labelWidth - selectedWidth - listInlinePadding - arrowWidth)
          : minGap;
        fit.fieldGap = Math.floor(fieldGap * 100) / 100;
        availableWidth = sortControlWidth - labelWidth - fieldGap - listInlinePadding - arrowWidth;
      }

      if (availableWidth <= 0 || selectedWidth <= availableWidth) return fit;

      const minFontSize = Math.max(12, baseFontSize * 0.82);
      const fittedFontSize = Math.max(minFontSize, Math.floor((baseFontSize * availableWidth / selectedWidth) * 100) / 100);

      if (fittedFontSize < baseFontSize) {
        fit.fontSize = fittedFontSize;
      }

      return fit;
    }

    applySelectedItemFit (fit) {
      if (!fit) return;
      if (fit.fieldGap != null) {
        fit.sortControl.style.setProperty('--facets-sorting-field-gap', `${fit.fieldGap}px`);
      }

      if (fit.fontSize != null) {
        this.style.setProperty('--facets-selected-option-font-size', `${fit.fontSize}px`);
      }
    }

    measureChoicesItemForFit () {
      const item = this.querySelector('.choices__list--single .choices__item');
      if (!item) return null;

      const list = this.querySelector('.choices__list--single');
      const listStyles = list ? window.getComputedStyle(list) : null;
      const listInlinePadding = listStyles
        ? (parseFloat(listStyles.paddingLeft) || 0) + (parseFloat(listStyles.paddingRight) || 0)
        : 0;

      return {
        selectedWidth: item.scrollWidth,
        baseFontSize: parseFloat(window.getComputedStyle(item).fontSize) || 0,
        listInlinePadding,
      };
    }

    measureNativeSelectedOptionForFit () {
      const selectedOption = this.select?.selectedOptions?.[0];
      const selectedText = selectedOption?.textContent?.trim();
      if (!selectedText) return null;

      const list = document.createElement('div');
      const item = document.createElement('div');
      list.className = 'choices__list choices__list--single';
      item.className = 'choices__item choices__item--selectable';
      item.textContent = selectedText;
      list.style.position = 'absolute';
      list.style.visibility = 'hidden';
      list.style.pointerEvents = 'none';
      list.style.whiteSpace = 'nowrap';
      list.style.left = '-9999px';
      list.style.top = '-9999px';
      item.style.whiteSpace = 'nowrap';
      list.appendChild(item);
      this.appendChild(list);

      const listStyles = window.getComputedStyle(list);
      const itemStyles = window.getComputedStyle(item);
      const measurement = {
        selectedWidth: item.scrollWidth,
        baseFontSize: parseFloat(itemStyles.fontSize) || 0,
        listInlinePadding: (parseFloat(listStyles.paddingLeft) || 0) + (parseFloat(listStyles.paddingRight) || 0),
      };

      list.remove();
      return measurement;
    }

    prepareSelectedItemFit () {
      this.resetSelectedItemFit();
      this.applySelectedItemFit(this.getSelectedItemFit(this.measureNativeSelectedOptionForFit()));
    }

    fitSelectedItem () {
      this.resetSelectedItemFit();

      if (!this.getSelectedItemFitContext()) {
        return;
      }

      this.applySelectedItemFit(this.getSelectedItemFit(this.measureChoicesItemForFit()));
    }

    buildParams (placeholder) {
      const params = {
        searchEnabled: false,
        position: 'bottom',
        shouldSort: false
      };

      if (placeholder) {
        params.placeholder = true;
        params.placeholderValue = placeholder;
        const prefix = placeholder;
        params.callbackOnCreateTemplates = (template) => ({
          item: (classNames, data) => {
            return template(`
            <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable}"
                 data-item data-id="${data.id}" data-value="${data.value}">
              <span class="choices__placeholder-custom">${prefix}:</span> ${data.label}
            </div>
          `);
          },
          choice: (classNames, data) => {
            return template(`
            <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable}"
                 data-select-text="${this.custom ? this.custom.config.itemSelectText : 'Press to select'}"
                 data-choice
                 data-id="${data.id}"
                 data-value="${data.value}"
                 ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}>
              ${data.label}
            </div>
          `);
          }
        });
      }

      return params;
    }
    reinitChoices (placeholder) {
      const currentValue = this.select ? this.select.value : null;

      if (this.custom && typeof this.custom.destroy === 'function') {
        this.custom.destroy();
        this.custom = null;
      }

      if (!this.select || typeof Choices !== 'function') return;

      const params = this.buildParams(placeholder);
      this.prepareSelectedItemFit();
      this.custom = new Choices(this.select, params);
      this.activePlaceholder = placeholder;
      this.scheduleFitSelectedItem();

      if (currentValue != null) {
        this.select.value = currentValue;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    handleResize () {
      const hasModeSpecific =
        this.getAttribute('data-placeholder-mobile') != null ||
        this.getAttribute('data-placeholder-desktop') != null;

      if (!hasModeSpecific) {
        this.scheduleFitSelectedItem();
        return;
      }

      const next = this.computePlaceholder();
      if (next !== this.activePlaceholder) {
        this.reinitChoices(next);
      } else {
        this.scheduleFitSelectedItem();
      }
    }

    computePlaceholder () {
      const common = this.getAttribute('data-placeholder') || '';
      const mobileAttr = this.getAttribute('data-placeholder-mobile');
      const desktopAttr = this.getAttribute('data-placeholder-desktop');
      const hasModeSpecific = mobileAttr != null || desktopAttr != null;
      if (hasModeSpecific) {
        if (this.isMobile()) {
          return (mobileAttr != null ? mobileAttr : common);
        } else {
          return (desktopAttr != null ? desktopAttr : common);
        }
      }
      return common;
    }

    connectedCallback () {
      this.select = this.querySelector('select');
      if (!this.select || typeof Choices !== 'function') return;

      const initialPlaceholder = this.computePlaceholder();
      this.reinitChoices(initialPlaceholder);

      this.select.addEventListener('change', this.handleChangeBound);
      window.addEventListener('resize', this.handleResizeBound);
    }

    disconnectedCallback () {
      if (this.select) {
        this.select.removeEventListener('change', this.handleChangeBound);
      }
      window.removeEventListener('resize', this.handleResizeBound);
      if (this.fitSelectedItemFrame) {
        cancelAnimationFrame(this.fitSelectedItemFrame);
        this.fitSelectedItemFrame = null;
      }

      if (this.custom && typeof this.custom.destroy === 'function') {
        this.custom.destroy();
        this.custom = null;
      }
    }
  }

  customElements.define('facet-custom-select', FacetCustomSelect);
}

class FacetFiltersForm extends HTMLElement {
  constructor () {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.container = null;
    this.quickFiltersScrollContainer = null;
    this.checkScroll = this.checkScroll.bind(this);
    this.checkQuickFiltersScroll = this.checkQuickFiltersScroll.bind(this);
    this.handleQuickFilterClick = this.handleQuickFilterClick.bind(this);
    // 400ms (was 800): still lets a quick second tick join the same request, but the grid starts updating sooner
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 400);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  checkScroll () {
    const scrollLeft = this.container.scrollLeft;
    const scrollWidth = this.container.scrollWidth;
    const clientWidth = this.container.clientWidth;

    this.container.classList.toggle('has-scroll-left', scrollLeft > 0);
    this.container.classList.toggle('has-scroll-right', scrollLeft + clientWidth < scrollWidth);
  }

  checkQuickFiltersScroll () {
    const container = this.quickFiltersScrollContainer;
    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const scrollLeft = container.scrollLeft;
    const hasScroll = maxScrollLeft > 1;

    this.classList.toggle('has-scroll', hasScroll);
    this.classList.toggle('has-scroll-left', scrollLeft > 1);
    this.classList.toggle('has-scroll-right', maxScrollLeft - scrollLeft > 1);
  }

  bindQuickFiltersScroll () {
    if (!this.classList.contains('quick-filters')) return;

    if (this.quickFiltersScrollContainer) {
      this.quickFiltersScrollContainer.removeEventListener('scroll', this.checkQuickFiltersScroll);
    }

    window.removeEventListener('resize', this.checkQuickFiltersScroll);
    this.quickFiltersScrollContainer = this.querySelector('.quick-filters__scroll');

    if (!this.quickFiltersScrollContainer) return;

    this.quickFiltersScrollContainer.addEventListener('scroll', this.checkQuickFiltersScroll);
    window.addEventListener('resize', this.checkQuickFiltersScroll);
    requestAnimationFrame(this.checkQuickFiltersScroll);
  }

  connectedCallback () {
    this.container = this.querySelector('.active-facets__blur');

    if (this.container) {
      this.container.addEventListener('scroll', this.checkScroll);
      window.addEventListener('resize', this.checkScroll);
      this.checkScroll();
    }

    if (this.classList.contains('quick-filters')) {
      this.addEventListener('click', this.handleQuickFilterClick);
      this.bindQuickFiltersScroll();
    }
  }

  disconnectedCallback () {
    if (this.container) {
      this.container.removeEventListener('scroll', this.checkScroll);
    }
    if (this.quickFiltersScrollContainer) {
      this.quickFiltersScrollContainer.removeEventListener('scroll', this.checkQuickFiltersScroll);
    }
    this.removeEventListener('click', this.handleQuickFilterClick);
    window.removeEventListener('resize', this.checkScroll);
    window.removeEventListener('resize', this.checkQuickFiltersScroll);
  }

  static setListeners () {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets (disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage (searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    document.querySelector('sticky-header').classList.add('loading-bar');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch (url, event) {
    fetch(url).then((response) => response.text()).then((responseText) => {
      const html = responseText;
      FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      document.querySelector('sticky-header').classList.remove('loading-bar');
    });
  }

  static renderSectionFromCache (filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    document.querySelector('sticky-header').classList.remove('loading-bar');

  }

  static renderProductGridContainer (html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').
      getElementById('ProductGridContainer').innerHTML;

    document.getElementById('ProductGridContainer').querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount (html) {
    const countElement = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (!countElement || !container) {
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      return;
    }

    const count = countElement.innerHTML;
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters (html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets (html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements (html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.quick-filters'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const quickFilters = document.querySelector('.quick-filters');
    if (quickFilters?.bindQuickFiltersScroll) quickFilters.bindQuickFiltersScroll();
    let quickFilterToCenter = null;

    if (quickFilters && FacetFiltersForm.quickFilterHandleToCenter) {
      quickFilterToCenter = Array.from(quickFilters.querySelectorAll('.quick-filters__button')).
        find((button) => button.dataset.quickFilterHandle === FacetFiltersForm.quickFilterHandleToCenter);
    }

    if (!quickFilterToCenter) {
      quickFilterToCenter = quickFilters?.querySelector('.quick-filters__button--active');
    }

    if (quickFilterToCenter) {
      FacetFiltersForm.centerQuickFilterButton(
        quickFilterToCenter,
        'smooth',
        Boolean(FacetFiltersForm.quickFilterHandleToCenter),
      );
    }

    FacetFiltersForm.quickFilterHandleToCenter = null;

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static centerQuickFilterButton (button, behavior = 'smooth', force = false) {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const container = button.closest('.quick-filters')?.querySelector('.quick-filters__scroll');
    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) return;

    const containerRect = container.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const isFullyVisible = buttonRect.left >= containerRect.left && buttonRect.right <= containerRect.right;

    if (!force && isFullyVisible) return;

    const nextScrollLeft =
      container.scrollLeft +
      buttonRect.left -
      containerRect.left -
      (container.clientWidth - buttonRect.width) / 2;

    container.scrollTo({
      left: Math.max(0, Math.min(nextScrollLeft, maxScrollLeft)),
      behavior,
    });
  }

  static setQuickFilterButtonPendingState (button) {
    const isActive = button.getAttribute('aria-pressed') === 'true' ||
      button.classList.contains('quick-filters__button--active');
    const nextIsActive = !isActive;

    button.classList.toggle('quick-filters__button--active', nextIsActive);
    button.classList.toggle('quick-filters__button--pending-inactive', !nextIsActive);
    button.setAttribute('aria-pressed', String(nextIsActive));
  }

  static renderCounts (source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement.querySelectorAll('.facets__item.hidden').
          forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts (source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash (searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections () {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams (form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm (searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler (event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    sortFilterForms.forEach((form) => {
      forms.push(this.createSearchParams(form));
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick (event) {
    event.preventDefault();
    const quickFilterButton = event.currentTarget?.closest?.('.quick-filters__button');

    if (quickFilterButton) {
      FacetFiltersForm.quickFilterHandleToCenter = quickFilterButton.dataset.quickFilterHandle || null;
      FacetFiltersForm.setQuickFilterButtonPendingState(quickFilterButton);
      FacetFiltersForm.centerQuickFilterButton(quickFilterButton, 'smooth', true);
    }

    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  handleQuickFilterClick (event) {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const button = target?.closest('.quick-filters__button');

    if (!button || !this.contains(button)) return;

    FacetFiltersForm.quickFilterHandleToCenter = button.dataset.quickFilterHandle || null;
    FacetFiltersForm.centerQuickFilterButton(button, 'smooth', true);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.quickFilterHandleToCenter = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor () {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange (event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown (event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues () {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues (input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor () {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter (event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
